import React from 'react';
import { useScope } from '../contexts/ScopeContext';

/**
 * RequireStore — wraps a route that can't function without a selected store.
 * Renders a "pick a store" prompt if scope.storeId is empty.
 *
 * Usage:
 *   <Route path="/inventory/waste" element={
 *     <RequireStore>
 *       <WasteAnalytics selectedStore={sid} enterpriseId={eid} />
 *     </RequireStore>
 *   } />
 */
export const RequireStore: React.FC<{ children: React.ReactNode; label?: string }> = ({ children, label }) => {
  const { ready, loading, storeId, stores } = useScope();

  if (!ready && loading) {
    return <div style={{ padding: '2rem', color: '#6b7280' }}>Loading stores…</div>;
  }

  if (!storeId) {
    return (
      <div style={{ padding: '2.5rem', textAlign: 'center', maxWidth: 520, margin: '3rem auto' }}>
        <h2 style={{ marginTop: 0 }}>Select a store</h2>
        <p style={{ color: '#6b7280' }}>
          {label || 'This page needs a store context.'} Pick a store from the
          dropdown at the top of the page to continue.
        </p>
        {stores.length === 0 && (
          <p style={{ color: '#991b1b', marginTop: '1rem' }}>
            No stores are available for your account. Contact your administrator if you
            believe this is a mistake.
          </p>
        )}
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * RequireEnterprise — same pattern for routes that need an enterprise scope.
 */
export const RequireEnterprise: React.FC<{ children: React.ReactNode; label?: string }> = ({ children, label }) => {
  const { ready, loading, enterpriseId, enterprises } = useScope();

  if (!ready && loading) {
    return <div style={{ padding: '2rem', color: '#6b7280' }}>Loading enterprise…</div>;
  }

  if (!enterpriseId) {
    return (
      <div style={{ padding: '2.5rem', textAlign: 'center', maxWidth: 520, margin: '3rem auto' }}>
        <h2 style={{ marginTop: 0 }}>Select an enterprise</h2>
        <p style={{ color: '#6b7280' }}>
          {label || 'This page needs an enterprise context.'} Pick one from the
          dropdown at the top of the page to continue.
        </p>
        {enterprises.length === 0 && (
          <p style={{ color: '#991b1b', marginTop: '1rem' }}>
            No enterprises are available for your account.
          </p>
        )}
      </div>
    );
  }

  return <>{children}</>;
};

export default RequireStore;
